// Lobby.tsx — The front door: start a solo game against AI empires, open a multiplayer
// lobby for friends (any seat nobody takes is filled by an AI at start), or join/resume/
// spectate an existing match. A match's own waiting room (MatchRoom) polls until the host
// starts it. Navigation between screens is a plain ?match=<id> URL so links can be shared.
import { useCallback, useEffect, useState } from 'react';
import {
  createMatch,
  deleteMatch,
  getMatch,
  joinMatch,
  leaveMatch,
  listMatches,
  startMatch,
  type MatchView,
} from '../api';

/** Navigate to a match (or back to the lobby with null), keeping test-only params. */
export function gotoMatch(id: string | null): void {
  const q = new URLSearchParams(window.location.search);
  q.delete('sandbox');
  if (id) q.set('match', id);
  else q.delete('match');
  const s = q.toString();
  window.location.assign(`${window.location.pathname}${s ? `?${s}` : ''}`);
}

const SIZES = [
  { label: 'Small', systems: 80 },
  { label: 'Medium', systems: 120 },
  { label: 'Large', systems: 180 },
];

function humans(m: MatchView): string {
  const seated = m.slots.filter((s) => !s.ai && !s.open).length;
  const seats = m.slots.filter((s) => !s.ai).length;
  return `${seated}/${seats} players · ${m.slots.length} empires`;
}

// Choice is a segmented picker (a row of toggle buttons) — few enough options that every
// one fits on screen, so no dropdown is needed.
function Choice({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: number; label: string }[];
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="choice" role="group" aria-label={label}>
      <span className="login-label">{label}</span>
      <div className="choice-row">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={o.value === value}
            className={o.value === value ? 'speed-btn active' : 'speed-btn'}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function NewGame({ onError }: { onError: (e: string) => void }) {
  const [name, setName] = useState('');
  const [empires, setEmpires] = useState(4);
  const [seats, setSeats] = useState(0);
  const [size, setSize] = useState(1);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const m = await createMatch({ name, empires, openSeats: Math.min(seats, empires - 1), systems: SIZES[size].systems });
      gotoMatch(m.id);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not create the game.');
      setBusy(false);
    }
  };

  return (
    <form className="lobby-form" onSubmit={submit}>
      <label className="login-label" htmlFor="fs-name">Galaxy name</label>
      <input id="fs-name" className="login-input" value={name} placeholder="Optional" maxLength={60} onChange={(e) => setName(e.target.value)} />
      <Choice label="Empires" options={[2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: String(n) }))} value={empires} onChange={setEmpires} />
      <Choice
        label="Other human players"
        options={Array.from({ length: empires }, (_, n) => ({ value: n, label: n === 0 ? 'None (solo)' : String(n) }))}
        value={Math.min(seats, empires - 1)}
        onChange={setSeats}
      />
      <Choice label="Galaxy" options={SIZES.map((sz, i) => ({ value: i, label: `${sz.label} · ${sz.systems}★` }))} value={size} onChange={setSize} />
      <button className="login-btn login-btn-primary" type="submit" disabled={busy}>
        {busy ? 'Charting the galaxy…' : seats === 0 ? 'Start solo game' : 'Open lobby'}
      </button>
    </form>
  );
}

/** The match list + new-game form. */
export function Lobby() {
  const [matches, setMatches] = useState<MatchView[] | null>(null);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    listMatches()
      .then(setMatches)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load games.'));
  }, []);
  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 5000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const act = async (fn: () => Promise<unknown>, then?: () => void) => {
    setError('');
    try {
      await fn();
      if (then) then();
      else refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };

  const mine = (matches ?? []).filter((m) => m.you);
  const open = (matches ?? []).filter((m) => !m.you && m.status === 'lobby' && m.slots.some((s) => s.open));
  const live = (matches ?? []).filter((m) => !m.you && m.status === 'running');

  return (
    <div className="lobby-overlay">
      <div className="lobby-panel">
        <div className="brand login-brand">
          <span className="brand-mark">✦</span>FARSTARS
        </div>
        <p className="lobby-tag">
          Settle worlds, build fleets, and conquer the galaxy. Win by holding 40% of all population, or by being the last empire
          standing.
        </p>
        {error ? <div className="login-error">{error}</div> : null}

        <h2 className="lobby-h">New game</h2>
        <NewGame onError={setError} />

        {mine.length > 0 ? (
          <>
            <h2 className="lobby-h">Your games</h2>
            <ul className="lobby-list">
              {mine.map((m) => (
                <li key={m.id} className="lobby-item">
                  <span className="lobby-name">{m.name}</span>
                  <span className="lobby-meta">
                    {m.status} · {humans(m)}
                  </span>
                  <span className="lobby-actions">
                    <button type="button" className="speed-btn" onClick={() => gotoMatch(m.id)}>
                      {m.status === 'lobby' ? 'Open' : m.status === 'finished' ? 'View' : 'Resume'}
                    </button>
                    {m.isHost ? (
                      <button type="button" className="speed-btn" onClick={() => act(() => deleteMatch(m.id))}>
                        Delete
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {open.length > 0 ? (
          <>
            <h2 className="lobby-h">Open lobbies</h2>
            <ul className="lobby-list">
              {open.map((m) => (
                <li key={m.id} className="lobby-item">
                  <span className="lobby-name">{m.name}</span>
                  <span className="lobby-meta">
                    host {m.hostName} · {humans(m)}
                  </span>
                  <span className="lobby-actions">
                    <button type="button" className="speed-btn" onClick={() => act(() => joinMatch(m.id), () => gotoMatch(m.id))}>
                      Join
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {live.length > 0 ? (
          <>
            <h2 className="lobby-h">Games in progress</h2>
            <ul className="lobby-list">
              {live.map((m) => (
                <li key={m.id} className="lobby-item">
                  <span className="lobby-name">{m.name}</span>
                  <span className="lobby-meta">{humans(m)}</span>
                  <span className="lobby-actions">
                    <button type="button" className="speed-btn" onClick={() => gotoMatch(m.id)}>
                      Spectate
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <a className="lobby-sandbox" href="?sandbox=1">
          Or explore the living galaxy sandbox →
        </a>
      </div>
    </div>
  );
}

/** A match's waiting room: seats, invite link, and the host's start button. */
export function MatchRoom({ initial }: { initial: MatchView }) {
  const [m, setM] = useState(initial);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      getMatch(m.id)
        .then((next) => {
          if (next.status !== 'lobby') window.location.reload();
          else setM(next);
        })
        .catch(() => {
          /* transient — keep polling */
        });
    }, 2000);
    return () => window.clearInterval(id);
  }, [m.id]);

  const act = async (fn: () => Promise<unknown>, then: () => void) => {
    setError('');
    try {
      await fn();
      then();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };
  const invite = `${window.location.origin}${window.location.pathname}?match=${m.id}`;
  const canJoin = !m.you && m.slots.some((s) => s.open);

  return (
    <div className="lobby-overlay">
      <div className="lobby-panel">
        <div className="brand login-brand">
          <span className="brand-mark">✦</span>FARSTARS
        </div>
        <h2 className="lobby-h">{m.name}</h2>
        <p className="lobby-tag">
          Waiting for players. Open seats left empty become AI empires when {m.isHost ? 'you start' : `${m.hostName} starts`} the game.
        </p>
        {error ? <div className="login-error">{error}</div> : null}
        <ul className="lobby-list">
          {m.slots.map((s) => (
            <li key={s.empireId} className="lobby-item">
              <span className="lobby-name">{s.open ? 'Open seat' : s.name}</span>
              <span className="lobby-meta">{s.you ? 'you' : s.ai ? 'AI empire' : s.open ? 'waiting…' : 'player'}</span>
            </li>
          ))}
        </ul>
        <div className="lobby-invite">
          <input className="login-input" readOnly value={invite} aria-label="Invite link" onFocus={(e) => e.target.select()} />
          <button
            type="button"
            className="speed-btn"
            onClick={() => {
              void navigator.clipboard?.writeText(invite);
              setCopied(true);
            }}
          >
            {copied ? 'Copied' : 'Copy invite'}
          </button>
        </div>
        {m.isHost ? (
          <button className="login-btn login-btn-primary" type="button" onClick={() => act(() => startMatch(m.id), () => window.location.reload())}>
            Start game
          </button>
        ) : null}
        {canJoin ? (
          <button className="login-btn login-btn-primary" type="button" onClick={() => act(() => joinMatch(m.id), () => window.location.reload())}>
            Take a seat
          </button>
        ) : null}
        {m.you && !m.isHost ? (
          <button className="login-btn" type="button" onClick={() => act(() => leaveMatch(m.id), () => gotoMatch(null))}>
            Leave lobby
          </button>
        ) : null}
        {m.isHost ? (
          <button className="login-btn" type="button" onClick={() => act(() => deleteMatch(m.id), () => gotoMatch(null))}>
            Cancel game
          </button>
        ) : null}
        <button className="login-btn" type="button" onClick={() => gotoMatch(null)}>
          ← All games
        </button>
      </div>
    </div>
  );
}
